import { Search } from 'lucide-react';

import { useT } from '@/core/providers/I18nProvider';

import type { ReactElement } from 'react';

/**
 * The phone's way into the same search.
 *
 * Below `md` the top bar is already carrying the drawer button, the brand mark,
 * the workspace switcher and the user menu; a text field would either push one
 * of those off the bar or be too narrow to type in. So the phone gets the icon
 * and the icon opens the ⌘K palette — a full-screen dialog, which is the shape
 * a phone can actually hold a result list in.
 *
 * It is the same index either way. What changes is the container, not what is
 * findable, which is the point: a phone operator must not have a smaller
 * dashboard.
 *
 * 44px on a coarse pointer per the house touch-target rule
 * (`ui-design.md` §6), 32px where there is a mouse — the same shape the shell's
 * other icon buttons take.
 */
export function SettingsSearchTrigger({ onOpen }: { readonly onOpen: () => void }): ReactElement {
  const { t } = useT();
  return (
    <button
      type="button"
      aria-label={t('settingsSearch.trigger')}
      onClick={onOpen}
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-hairline bg-surface text-foreground transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring [@media(pointer:coarse)]:size-11"
    >
      <Search aria-hidden="true" className="h-4 w-4" />
    </button>
  );
}
